import { SCROLL_AIR_ATTRIBUTE } from '@affine/core/modules/streamlain/shared/workspace-pane-layout';
import clsx from 'clsx';
import {
  type CSSProperties,
  forwardRef,
  type HTMLAttributes,
  memo,
  useEffect,
  useRef,
} from 'react';
import type { ScrollSeekPlaceholderProps, VirtuosoProps } from 'react-virtuoso';

import type { PDFMeta } from '../renderer';
import type { PageSize } from '../renderer/types';
import {
  type PdfPageBitmapRole,
  readPdfPageBitmapCache,
} from './page-bitmap-cache';
import * as styles from './styles.css';

export type PDFVirtuosoContext = {
  viewportInfo: PageSize;
  meta: PDFMeta;
  resize: (
    viewportInfo: PageSize,
    actualSize: PageSize,
    maxSize: PageSize,
    isThumbnail?: boolean
  ) => { aspectRatio: number } & PageSize;
  isThumbnail?: boolean;
  pdfId?: string;
  bitmapCacheRole?: PdfPageBitmapRole;
  pageClassName?: string;
  listClassName?: string;
  itemClassName?: string;
  onPageSelect?: (index: number) => void;
};

export type PDFVirtuosoProps = VirtuosoProps<unknown, PDFVirtuosoContext>;

export const Scroller = forwardRef<HTMLDivElement, PDFVirtuosoProps>(
  ({ context, className, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={clsx(
          styles.pdfVirtuosoScroller,
          context?.isThumbnail && styles.pdfVirtuosoScrollerHiddenScrollbar,
          className
        )}
        {...props}
      />
    );
  }
);

Scroller.displayName = 'pdf-virtuoso-scroller';

export const ScrollSeekPlaceholder = forwardRef<
  HTMLDivElement,
  ScrollSeekPlaceholderProps & {
    context?: PDFVirtuosoContext;
  }
>(({ context, index }, ref) => {
  const className = context?.pageClassName;
  const isThumbnail = context?.isThumbnail;
  const size = context?.meta.pageSizes[index];
  const maxSize = context?.meta.maxSize;
  const height = size?.height ?? 759;
  const style =
    context?.viewportInfo && size && maxSize
      ? context.resize(context.viewportInfo, size, maxSize, isThumbnail)
      : undefined;
  const displayWidth = typeof style?.width === 'number' ? style.width : null;
  const displayHeight = typeof style?.height === 'number' ? style.height : null;
  const pixelRatio =
    typeof window === 'undefined' ? 1 : Math.max(1, window.devicePixelRatio);
  const cacheWidth =
    displayWidth === null ? null : Math.ceil(displayWidth * pixelRatio);
  const bitmapCacheRole =
    context?.bitmapCacheRole ?? (isThumbnail ? 'thumbnail' : 'page');
  const cachedBitmap =
    context?.pdfId && cacheWidth !== null
      ? readPdfPageBitmapCache(
          context.pdfId,
          index,
          bitmapCacheRole,
          cacheWidth
        )
      : null;

  return (
    <Item
      data-index={index}
      data-known-size={height}
      data-item-index={index}
      style={{ overflowAnchor: 'none' }}
      ref={ref}
    >
      <div className={className} style={style}>
        <PdfPageBitmapSurface
          bitmap={cachedBitmap}
          displayWidth={displayWidth}
          displayHeight={displayHeight}
          data-pdf-scroll-seek-cache={cachedBitmap ? 'true' : 'false'}
        />
      </div>
    </Item>
  );
});

ScrollSeekPlaceholder.displayName = 'pdf-virtuoso-scroll-seek-placeholder';

export const List = forwardRef<HTMLDivElement, PDFVirtuosoProps>(
  ({ context, className, ...props }, ref) => {
    return (
      <div
        className={clsx([
          styles.virtuosoList,
          context?.listClassName,
          className,
        ])}
        ref={ref}
        {...props}
      />
    );
  }
);

List.displayName = 'pdf-virtuoso-list';

export const ListWithSmallGap = forwardRef<HTMLDivElement, PDFVirtuosoProps>(
  ({ context: _, className, ...props }, ref) => {
    return (
      <List className={clsx([className, 'small-gap'])} ref={ref} {...props} />
    );
  }
);

ListWithSmallGap.displayName = 'pdf-virtuoso-small-gap-list';

export const Item = forwardRef<HTMLDivElement, PDFVirtuosoProps>(
  ({ context, className, ...props }, ref) => {
    return (
      <div
        className={clsx([
          styles.virtuosoItem,
          context?.itemClassName,
          className,
        ])}
        ref={ref}
        {...props}
      />
    );
  }
);

Item.displayName = 'pdf-virtuoso-item';

export const ListPadding = () => (
  <div style={{ width: '100%', height: '20px' }} />
);

/** The list's end: its padding plus the dock's room, so the last page scrolls clear of the dock. */
export const ListEndPadding = () => (
  <div
    style={{ width: '100%', height: '20px' }}
    {...{ [SCROLL_AIR_ATTRIBUTE]: '' }}
  />
);

export const LoadingSvg = memo(
  ({ className, style }: { className?: string; style?: CSSProperties }) => {
    return (
      <svg
        className={clsx([styles.pdfLoading, className])}
        style={style}
        width="16"
        height="24"
        viewBox="0 0 537 759"
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
      >
        <rect width="537" height="759" fill="#fff" />
        <g fill="#d8dee8">
          <rect x="32" y="82" width="361" height="30" rx="4" />
          <rect x="32" y="142" width="444" height="30" rx="4" />
          <rect x="32" y="202" width="387" height="30" rx="4" />
          <rect x="32" y="262" width="461" height="30" rx="4" />
          <rect x="32" y="322" width="282" height="30" rx="4" />
          <rect x="32" y="382" width="361" height="30" rx="4" />
          <rect x="32" y="442" width="444" height="30" rx="4" />
          <rect x="32" y="502" width="240" height="30" rx="4" />
          <rect x="32" y="562" width="201" height="30" rx="4" />
          <rect x="32" y="622" width="224" height="30" rx="4" />
          <rect x="314" y="502" width="191" height="166" rx="4" />
        </g>
      </svg>
    );
  }
);

LoadingSvg.displayName = 'pdf-loading';

export const PdfPageBitmapSurface = memo(function PdfPageBitmapSurface({
  bitmap,
  displayWidth,
  displayHeight,
  ...props
}: {
  bitmap: ImageBitmap | null;
  displayWidth?: number | null;
  displayHeight?: number | null;
} & HTMLAttributes<HTMLDivElement>) {
  const hasDisplaySize =
    typeof displayWidth === 'number' && typeof displayHeight === 'number';
  const surfaceStyle = hasDisplaySize
    ? ({
        width: displayWidth,
        height: displayHeight,
        aspectRatio: `${displayWidth} / ${displayHeight}`,
      } satisfies CSSProperties)
    : undefined;

  return (
    <div
      className={clsx([
        styles.pdfPageCanvasLayer,
        !bitmap && styles.pdfPageBlankSurface,
      ])}
      style={surfaceStyle}
      {...props}
    >
      {bitmap ? <CachedPageBitmapCanvas bitmap={bitmap} /> : null}
    </div>
  );
});

function CachedPageBitmapCanvas({ bitmap }: { bitmap: ImageBitmap }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    canvas.width = bitmap.width;
    canvas.height = bitmap.height;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(bitmap, 0, 0);
  }, [bitmap]);

  return (
    <PDFPageCanvas
      ref={canvasRef}
      style={{
        width: '100%',
        height: '100%',
      }}
    />
  );
}

export const PDFPageCanvas = forwardRef<
  HTMLCanvasElement,
  { style?: CSSProperties }
>(({ style, ...props }, ref) => {
  return (
    <canvas
      className={styles.pdfPageCanvas}
      ref={ref}
      style={style}
      {...props}
    />
  );
});

PDFPageCanvas.displayName = 'pdf-page-canvas';
